import { contactRelevance } from './scoring.js';
import { normalizeCompanyName } from './normalise.js';
import { isOpportunitySnoozed, opportunityTimingStage } from './opportunity-timing.js';
import { activeCompanyTeamStatus, latestCompanyTeamStatus } from './company-team-status.js';
import { companyTeamKey } from './account-hierarchy.js';
import { learnedRecommendationAdjustment } from './learning-recommender.js';
function opportunityTargets(opportunity) {
    const names = [opportunity.companyName, ...(opportunity.targetCompanies || []).map(target => target.name)]
        .map(value => normalizeCompanyName(value || ''))
        .filter(Boolean);
    return [...new Set(names)];
}
export function recommendContacts(contacts, sequences, opportunities, limit = 10, companies = []) {
    const activeContactIds = new Set(sequences.filter(x => !x.archived && x.stage !== 'sent').map(x => x.contactId));
    const contactById = new Map(contacts.map(contact => [contact.id, contact]));
    const companyById = new Map(companies.map(company => [company.id, company]));
    const scheduledByCompany = new Map();
    const scheduledByCompanyCategory = new Map();
    const touchedByCompany = new Map();
    for (const sequence of sequences) {
        if (sequence.archived)
            continue;
        const sequenceContact = contactById.get(sequence.contactId);
        const touchedCompanyKey = String(sequence.companyId || sequenceContact?.companyId || normalizeCompanyName(sequenceContact?.companyName || ''));
        if (touchedCompanyKey && (sequence.stage === 'sent' || sequence.stage === 'scheduled'))
            touchedByCompany.set(touchedCompanyKey, (touchedByCompany.get(touchedCompanyKey) || 0) + 1);
        if (sequence.stage !== 'scheduled')
            continue;
        const companyKey = String(sequence.companyId || sequenceContact?.companyId || normalizeCompanyName(sequenceContact?.companyName || ''));
        if (!companyKey)
            continue;
        scheduledByCompany.set(companyKey, (scheduledByCompany.get(companyKey) || 0) + 1);
        const category = String(sequence.crmCategory || sequenceContact?.crmCategory || 'Misc');
        const laneKey = companyTeamKey(companyKey, category);
        scheduledByCompanyCategory.set(laneKey, (scheduledByCompanyCategory.get(laneKey) || 0) + 1);
    }
    const liveOpportunities = opportunities.filter(opportunity => {
        if (isOpportunitySnoozed(opportunity))
            return false;
        const stage = opportunityTimingStage(opportunity);
        return stage === 'contact-now' || stage === 'opening-soon';
    });
    const now = Date.now();
    return contacts
        .filter(c => {
        if (c.archived || c.contactedAt || c.doNotContact || activeContactIds.has(c.id))
            return false;
        const company = companyById.get(c.companyId);
        if (activeCompanyTeamStatus(company, c.crmCategory, now))
            return false;
        const snoozedUntil = Date.parse(c.prioritySnoozedUntil || '');
        return !Number.isFinite(snoozedUntil) || snoozedUntil <= now;
    })
        .map(contact => {
        let score = contactRelevance(contact.title);
        const reasons = [];
        const company = normalizeCompanyName(contact.companyName);
        const linked = liveOpportunities.filter(opportunity => opportunityTargets(opportunity).includes(company));
        const contactNow = linked.find(opportunity => opportunityTimingStage(opportunity) === 'contact-now');
        const openingSoon = linked.find(opportunity => opportunityTimingStage(opportunity) === 'opening-soon');
        if (contactNow) {
            score += 16;
            reasons.push(`Contact now · ${contactNow.name}`);
        }
        else if (openingSoon) {
            score += 10;
            reasons.push(`Opening soon · ${openingSoon.name}`);
        }
        if (contact.email) {
            score += 5;
            reasons.push('Email already known');
        }
        if (contact.emailVerification === 'verified') {
            score += 7;
            reasons.push('Verified email');
        }
        if (['partnerships', 'experiential', 'marketing', 'creative'].includes(contact.department)) {
            score += 5;
            reasons.push(`${contact.department} role`);
        }
        // Keep Priority People diversified once an account already has enough outreach in flight.
        // Two people in the same category starts the lane penalty; three people across the company
        // starts the broader account penalty. Exceptional fit/opportunity relevance can still win.
        const companyKey = String(contact.companyId || normalizeCompanyName(contact.companyName));
        const category = String(contact.crmCategory || 'Misc');
        const scheduledCompany = scheduledByCompany.get(companyKey) || 0;
        const scheduledLane = scheduledByCompanyCategory.get(companyTeamKey(companyKey, contact.crmCategory)) || 0;
        if ((touchedByCompany.get(companyKey) || 0) === 0) {
            score += 5;
            reasons.push('Untouched company');
        }
        if (scheduledLane >= 2) {
            score -= 18 + (scheduledLane - 2) * 6;
            reasons.push(`${scheduledLane} ${category} contacts already scheduled at ${contact.companyName}`);
        }
        if (scheduledCompany >= 3) {
            score -= 10 + (scheduledCompany - 3) * 4;
            reasons.push(`${scheduledCompany} people already scheduled at ${contact.companyName} · broader account mix preferred`);
        }
        const teamContext = latestCompanyTeamStatus(companyById.get(contact.companyId), contact.crmCategory);
        if (teamContext && Date.parse(teamContext.activeUntil) <= now) {
            score += 4;
            reasons.push(`Revisit · previous ${contact.crmCategory} Not now has expired · custom outreach required`);
        }
        // Fixed fit/safety rules remain the foundation. Historical outcomes only
        // nudge ranking once there is enough evidence, so the system learns without
        // allowing a small sample to overpower the rules Bailey has defined.
        const learned = learnedRecommendationAdjustment(contact, sequences, contacts, opportunities);
        score += learned.delta;
        if (learned.delta !== 0)
            reasons.push(...learned.reasons);
        const roleScore = contactRelevance(contact.title);
        if (/\bmotorsport\b/i.test(contact.title))
            reasons.push('Motorsport remit');
        if (roleScore >= 75)
            reasons.unshift('Strong role relevance');
        else if (roleScore >= 60)
            reasons.unshift('Relevant role');
        return { contact, score, reasons: [...new Set(reasons)], learning: { delta: learned.delta, confidence: learned.confidence, evidence: learned.evidence, totalSent: learned.totalSent, enoughEvidence: learned.enoughEvidence, reasons: learned.reasons } };
    })
        .sort((a, b) => b.score - a.score || a.contact.companyName.localeCompare(b.contact.companyName) || a.contact.fullName.localeCompare(b.contact.fullName))
        .slice(0, Math.max(1, limit))
        .map(item => ({ ...item, score: Math.max(0, Math.min(100, item.score)) }));
}
